import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from 'i18next';
import { useMutation } from '@apollo/client';
import { Button, TextInput } from '@unmind/design-system';
import { isMSTeams } from 'utils/MSTeams';
import { getChannel } from '../../utils';
import { tracking } from '../../App/Tracking';
import { LOG_EVENT_MUTATION } from '../../App/Tracking/serverside/withLogEvent';
import getSubdomainFromUrl from '../../utils/getSubdomainFromUrl';

import './CopyLink.scss';

export const MS_TEAMS_INVITE_COLLEAGUE_URL =
  'https://teams.microsoft.com/l/app/c7d6307e-8186-438f-a33f-66b22f269324';

export const CopyLink = () => {
  const { t: translate } = useTranslation<Namespace<'shared'>>('shared');
  const [logEvent] = useMutation(LOG_EVENT_MUTATION);

  const buttonTextDuringTimeout = translate('copy_link.on_click');
  const buttonText = translate('copy_link.default');

  const [currentButtonText, setButtonText] = useState(buttonText);
  const [timeOutIsExecuting, setTimeOutIsExecuting] = useState(false);

  const subdomain = getSubdomainFromUrl();
  // show the msteams app link when web app is rendered in msteams
  const url = isMSTeams()
    ? MS_TEAMS_INVITE_COLLEAGUE_URL
    : `https://${subdomain}.${getChannel().domain}/start?utm_source=colleague_referral`;

  let timer = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);

  const onButtonTextClicked = async () => {
    if (!timeOutIsExecuting) {
      setTimeOutIsExecuting(true);
      setButtonText(buttonTextDuringTimeout);

      await navigator.clipboard.writeText(url);

      tracking.track('invite-a-colleague-share-link-clicked');
      void logEvent({
        variables: {
          input: {
            eventName: 'COLLEAGUE_INVITE_CTA_CLICKED',
          },
        },
      });

      timer = window.setTimeout(() => {
        setButtonText(buttonText);
        setTimeOutIsExecuting(false);
      }, 3000);
    }
  };

  useEffect(() => () => window.clearTimeout(timer), [timer]);

  const icon = timeOutIsExecuting ? 'check' : undefined;

  return (
    <div className="copy-link">
      <TextInput
        id="copy-link-url"
        name="URL"
        value={url}
        size="medium"
        readOnly={true}
        className={timeOutIsExecuting ? 'copy-link-url-copied' : ''}
        ref={selectRef}
        aria-label="URL"
      />
      <Button
        className="copy-link-button"
        variant="primary"
        onClick={onButtonTextClicked}
        label={currentButtonText}
        icon={icon}
      />
    </div>
  );
};
